import type { Texas42Database } from './db.js';
import { computeChecksum, verifyChecksum } from './checksum.js';
import { createEnvelope, type PersistedEnvelope } from './records.js';
import type { WriteQueue } from './write-queue.js';
import { CorruptRecordError, StorageUnavailableError } from './storage-errors.js';
import { quarantineRecord } from './quarantine.js';
import type { QuarantineRecordType } from '@texas42/app-model';
import { toPlainJson } from './plain.js';

export async function writeEnvelope<T extends { id?: string; key?: string; profileId?: string }>(
  queue: WriteQueue,
  db: Texas42Database,
  storeName: Parameters<Texas42Database['put']>[0],
  payload: T,
): Promise<void> {
  await queue.enqueue(async () => {
    try {
      // Callers often pass Svelte $state proxies; IndexedDB cannot structured-clone them.
      const plain = toPlainJson(payload);
      const checksum = await computeChecksum(plain);
      const envelope = createEnvelope(plain, checksum);
      await db.put(storeName, envelope as never);
    } catch (error) {
      throw new StorageUnavailableError('Unable to save to local storage.', error);
    }
  });
}

export async function readEnvelope<T>(
  db: Texas42Database,
  storeName: Parameters<Texas42Database['get']>[0],
  key: string,
  recordType: QuarantineRecordType,
  recordId: string,
): Promise<T | null> {
  const envelope = (await db.get(storeName, key)) as PersistedEnvelope<T> | undefined;
  if (!envelope) {
    return null;
  }

  const valid = await verifyChecksum(envelope.payload, envelope.checksum);
  if (!valid) {
    await quarantineRecord(db, recordType, recordId, 'Checksum verification failed', envelope);
    await db.delete(storeName, key);
    throw new CorruptRecordError(recordType, recordId, 'Checksum verification failed');
  }

  return envelope.payload;
}

export async function readEnvelopeOrNull<T>(
  db: Texas42Database,
  storeName: Parameters<Texas42Database['get']>[0],
  key: string,
  recordType: QuarantineRecordType,
  recordId: string,
): Promise<T | null> {
  try {
    return await readEnvelope<T>(db, storeName, key, recordType, recordId);
  } catch (error) {
    if (error instanceof CorruptRecordError) {
      return null;
    }
    throw error;
  }
}

export async function listEnvelopes<T>(
  db: Texas42Database,
  storeName: Parameters<Texas42Database['getAll']>[0],
  recordType: QuarantineRecordType,
  idFromPayload: (payload: T) => string,
): Promise<T[]> {
  const envelopes = (await db.getAll(storeName)) as Array<PersistedEnvelope<T>>;
  const results: T[] = [];

  for (const envelope of envelopes) {
    const id = idFromPayload(envelope.payload);
    const valid = await verifyChecksum(envelope.payload, envelope.checksum);
    if (!valid) {
      await quarantineRecord(db, recordType, id, 'Checksum verification failed', envelope);
      await db.delete(storeName, id);
      continue;
    }
    results.push(envelope.payload);
  }

  return results;
}
